// Formatting for the GPA/CGPA summary strip on a tertiary result.
//
// Summary rows are template-defined: a school can configure numeric rows (GPA,
// CGPA, total units) alongside textual ones (degree classification, remark).
// Coercing every row through Number().toFixed(2) rendered "NaN" for the textual
// ones, so numeric formatting is applied only where it is actually numeric.

export interface ResultSummaryItem {
  label: string;
  value: string | null;
}

/** A summary row as it arrives from the (Mixed-typed) GraphQL response. */
interface RawSummaryEntry {
  label?: string | null;
  value?: unknown;
}

function isNumericValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'boolean') return false;

  return !Number.isNaN(Number(value));
}

/**
 * Normalise a template-driven summary object into label/value pairs ready for
 * display. Rows without a label, or whose value key is absent entirely, are
 * dropped; a row with an explicit null value is kept so the UI can show "N/A".
 */
export function formatSummaryItems(summary: unknown): ResultSummaryItem[] {
  if (!summary || typeof summary !== 'object') return [];

  const items: ResultSummaryItem[] = [];

  for (const entry of Object.values(summary as Record<string, RawSummaryEntry>)) {
    if (!entry || typeof entry !== 'object') continue;
    if (!entry.label) continue;
    if (!('value' in entry) || entry.value === undefined) continue;

    const raw = entry.value;

    items.push({
      label: entry.label,
      value: raw === null
        ? null
        : (isNumericValue(raw) ? Number(raw).toFixed(2) : String(raw)),
    });
  }

  return items;
}
